import { useMemo } from "react";
import {
  cn,
  formatCurrency,
  getColorFromCssVariable,
  HorizontalBarsChart,
} from "ui";
import { ChartCard } from "../ChartCard";
import { HorizontalBarsLoadingSkeleton } from "../HorizontalBarsLoadingSkeleton";
import { useDatadogQuery } from "./useDatadogQuery";
import { twoFixedDigit } from "../../../../utils/twoFixedDigit";

type MethodsData = {
  label: string;
  value: number;
}[];

export function MethodsChart() {
  const { ref, queryStatus, data, error, refetch } =
    useDatadogQuery<MethodsData>({
      chartType: "TOP_PAYMENT_METHODS",
      delayMs: 300,
    });

  const color = getColorFromCssVariable("--chart-categorical-4-subtler");

  const { chartData, topMethod } = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) {
      return { chartData: [], topMethod: null };
    }

    const total = data.reduce((sum, item) => sum + item.value, 0);

    const transformedData = data.map((item) => ({
      label: item.label,
      value: item.value,
      percentage: total > 0 ? (item.value / total) * 100 : 0,
      color,
    }));

    const topMethod = transformedData[0] || null;
    return { chartData: transformedData, topMethod };
  }, [data, color]);

  return (
    <ChartCard
      ref={ref}
      title={
        topMethod
          ? `${twoFixedDigit(topMethod.percentage)}% ${topMethod.label}`
          : "Top"
      }
      subTitle="Methods"
      contentClassName={cn(
        "overflow-y-auto",
        chartData.length > 0 && "h-auto max-h-80"
      )}
      queryStatus={queryStatus}
      loadingSkeleton={<HorizontalBarsLoadingSkeleton />}
      error={error}
      retry={refetch}
    >
      <HorizontalBarsChart
        items={chartData}
        formatValue={(item) =>
          formatCurrency({
            currency: "USD",
            amount: item.value,
            compact: true,
          })
        }
      />
    </ChartCard>
  );
}
